import './LogsTracker.scss';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SearchField } from '@Components/SearchField/SearchField';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { LOGS_TRACKER_PATH, LogType, View, VIEW_OPTIONS, ViewType } from './logs-tracker-constants';
import { useLogsLane } from './use-logs-lane';
import { LogsLane } from './LogsLane';

const parseView = (subPath: string | undefined): ViewType =>
  subPath === View.PRE || subPath === View.POST ? subPath : View.BOTH;

export const LogsTracker = () => {
  const { '*': subPath } = useParams();
  const navigate = useNavigate();
  const view = parseView(subPath);
  const [search, setSearch] = useState('');

  // Both lanes stay loaded in every view so switching views never drops unsaved edits.
  const preLane = useLogsLane(LogType.PRE);
  const postLane = useLogsLane(LogType.POST);
  const visibleLanes = [preLane, postLane].filter(lane => view === View.BOTH || lane.type === view);
  const hasEdits = preLane.hasEdits || postLane.hasEdits;

  useEffect(() => {
    if (!hasEdits) {
      return undefined;
    }
    const warnBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasEdits]);

  const changeView = (nextView: ViewType) => {
    navigate(nextView === View.BOTH ? LOGS_TRACKER_PATH : `${LOGS_TRACKER_PATH}/${nextView}`);
    Mixpanel.track(MxE.TabChange, { page: PAGE_NAMES.LOGS_TRACKER, option_selected: nextView });
  };

  return (
    <Page
      title={PAGE_NAMES.LOGS_TRACKER}
      pageType={PAGE_NAMES.LOGS_TRACKER}
      isLoading={preLane.isLoading || postLane.isLoading}
      actions={
        <div className='logs-tracker-actions'>
          <SearchField
            className='logs-tracker-search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search networks or companies'
          />
          <SegmentedControlButton
            options={VIEW_OPTIONS}
            selectedOption={view}
            onChange={changeView}
          />
        </div>
      }
    >
      <div className='logs-tracker'>
        {visibleLanes.map(lane => (
          <LogsLane key={lane.type} lane={lane} search={search} />
        ))}
      </div>
    </Page>
  );
};
